import { create } from "zustand"

import {
  ACCESS_TOKEN_KEY,
  APP_VERSION,
  BE_CLIENT_ID,
  BE_OPERATOR_ID,
  BE_OPERATOR_TOKEN,
  ENV,
} from "@be-miniapp/app-configs"
import {
  DEFAULT_HCM_LOCATION,
  type LocationSource,
} from "@be-miniapp/shared/utils/location-policy"
import { generateRandomDeviceID } from "@be-miniapp/shared/utils/misc"
import { DeviceType, type Location } from "@be-miniapp/types"

import { fromSemverToVersion } from "@/utils/version"
import {
  DEFAULT_PARTNER,
  getPartnerSourceApp,
  validatePartnerIdentifier,
} from "@/constants/partner"

// Note : this store is mainly using for storing information that need to be sent to the server.
// This data will be included in all requests sending to the server.
interface ClientInfoStore {
  locationSource: LocationSource
  additionals: {
    client_info: {
      env: string
      platform: "android-web" | "ios-web" | "android" | "ios"
      device_type: string
      os_version?: string
      device_token: string
      app_version: string
      grab_installed?: string
      source_app: string
      modelId?: string
      locale: string
      device_name: "ios" | "android"
      device_rooted?: string
      version: string
      client_id?: string
      customer_package_name: string
      sdkVersion?: string
      ip?: string
      operator_token?: string
      macAddress?: string
      integrated: string
      unique_device_id: string
      location?: Location
      operator_id?: number
    }
    location?: Location
    latitude?: number
    longitude?: number
    operator_token?: string
    client_id?: string
    device_token?: string
    platform: "android-web" | "ios-web" | "android" | "ios"
    env: string
    version: string
    app_version: string
    locale: "vi" | "en"
    device_type: string
    source_app: string
    customer_package_name: string
    operator_id?: number
    device_name?: string
    unique_device_id?: string
    access_token?: string
  }

  setDeviceToken: (device_token: string) => void
  setLocale: (locale: "vi" | "en") => void
  setLocation: (
    location: Location,
    source?: Exclude<LocationSource, "fallback">
  ) => void
  resetLocationToFallback: () => void
  setAccessToken: (access_token: string | undefined) => void
  setPlatform: (platform: "android" | "ios") => void
  setVersion: (version: string) => void
  setSourceApp: (source_app: string) => void
}

const getSourceApp = () => {
  if (typeof window === "undefined") return DEFAULT_PARTNER
  const params = new URLSearchParams(window.location?.search)
  const rawIdentifier =
    params.get("ptr") ||
    params.get("partner_identifier") ||
    localStorage?.getItem("partner_identifier") ||
    DEFAULT_PARTNER
  const validated = validatePartnerIdentifier(rawIdentifier)
  return getPartnerSourceApp(validated)
}

const useClientInfo = create<
  ClientInfoStore,
  [["zustand/persist", ClientInfoStore]]
>((set, get) => ({
  locationSource: "fallback",
  additionals: {
    locale: "vi",
    app_version: `${fromSemverToVersion(APP_VERSION)}`,
    os_version: "web",
    operator_token: BE_OPERATOR_TOKEN,
    client_id: BE_CLIENT_ID,
    device_type: DeviceType.web,
    device_token: generateRandomDeviceID(),
    platform: "ios",
    env: ENV,
    operator_id: BE_OPERATOR_ID,
    version: APP_VERSION,
    device_name: "ios",
    device: {
      device_id: generateRandomDeviceID(),
    },
    client_info: {
      env: ENV,
      operator_id: BE_OPERATOR_ID,
      device_name: "ios",
      os_version: "web",
      device_rooted: "0",
      platform: "ios",
      app_version: `${fromSemverToVersion(APP_VERSION)}`,
      operator_token: BE_OPERATOR_TOKEN,
      client_id: BE_CLIENT_ID,
      device_type: DeviceType.web,
      source_app: getSourceApp(),
      device_token: generateRandomDeviceID(),

      locale: "vi",
      version: APP_VERSION,
      customer_package_name: "be-appview",
      integrated: "1",
      unique_device_id: generateRandomDeviceID(),
      location: { ...DEFAULT_HCM_LOCATION },
    },
    location: { ...DEFAULT_HCM_LOCATION },
    latitude: DEFAULT_HCM_LOCATION.latitude,
    longitude: DEFAULT_HCM_LOCATION.longitude,
    customer_package_name: "be-appview",
    device_rooted: "0",
    integrated: "1",
    unique_device_id: generateRandomDeviceID(),
    source_app: getSourceApp(),
  },
  setAccessToken: (access_token: string | undefined) => {
    // Store in localStorage for axios interceptor
    if (access_token) {
      localStorage?.setItem(ACCESS_TOKEN_KEY, access_token)
    } else {
      localStorage?.removeItem(ACCESS_TOKEN_KEY)
    }

    // Update the store state
    set({
      additionals: {
        ...get().additionals,
        access_token,
      },
    })
  },
  setDeviceToken: (device_token: string) =>
    set({
      additionals: {
        ...get().additionals,
        client_info: {
          ...get().additionals.client_info,
          device_token,
          unique_device_id: device_token,
        },
        device_token,
        unique_device_id: device_token,
      },
    }),
  setLocale: (locale: "vi" | "en") =>
    set({
      additionals: {
        ...get().additionals,
        client_info: { ...get().additionals.client_info, locale },
        locale,
      },
    }),
  setLocation: (location: Location, source = "sdk") =>
    set({
      locationSource: source,
      additionals: {
        ...get().additionals,
        client_info: { ...get().additionals.client_info, location },
        location,
        latitude: location.latitude,
        longitude: location.longitude,
      },
    }),
  resetLocationToFallback: () =>
    set({
      locationSource: "fallback",
      additionals: {
        ...get().additionals,
        client_info: {
          ...get().additionals.client_info,
          location: { ...DEFAULT_HCM_LOCATION },
        },
        location: { ...DEFAULT_HCM_LOCATION },
        latitude: DEFAULT_HCM_LOCATION.latitude,
        longitude: DEFAULT_HCM_LOCATION.longitude,
      },
    }),
  setPlatform: (platform: "android" | "ios") =>
    set({
      additionals: {
        ...get().additionals,
        client_info: {
          ...get().additionals.client_info,
          platform: platform,
          device_name: platform !== "ios" ? "android" : "ios",
          device_type: DeviceType.web,
        },
        platform: platform,
        device_name: platform !== "ios" ? "android" : "ios",
        device_type: DeviceType.web,
      },
    }),
  setVersion: (version: string) =>
    set({
      additionals: {
        ...get().additionals,
        client_info: {
          ...get().additionals.client_info,
          version: version,
          app_version: `${fromSemverToVersion(version)}`,
        },
        version: version,
        app_version: `${fromSemverToVersion(version)}`,
      },
    }),
  setSourceApp: (source_app: string) =>
    set({
      additionals: {
        ...get().additionals,
        source_app,
        client_info: { ...get().additionals.client_info, source_app },
      },
    }),
}))

export { useClientInfo }
